import { APIError } from '@cartwhl/common/api/apiRequest';
import postMotionFromText, {
  type ExportType as ApiExportType,
  type ExportSettings,
} from '@cartwhl/common/api/postMotionFromText';
import { navigateTo } from '@cartwhl/common/navigation';
import { BaseStore } from '@cartwhl/common/stores/BaseStore';
import { createStoreProvider } from '@cartwhl/common/stores/createStoreProvider';
import type { FrameRate } from '@cartwhl/common/types';
import { waitForAsyncState } from '@cartwhl/common/utils';
import { handleOrgCreditBlockApiError } from '@cartwhl/common/utils/handleOrgCreditBlockApiError';

import * as globalStores from '../../stores/global';
import type {
  BatchGenerationModel,
  ExportType,
  MotionPromptRow,
  UploadModalState,
} from './types';

const EXPORT_SETTINGS_STORAGE_KEY =
  'cartwheel_batch_generation_export_settings';

type PersistedExportSettings = {
  fileType: ExportType;
  frameRate: FrameRate;
  keyframeSimplification: 'none' | 'simplify';
  includeMesh: boolean;
};

const loadExportSettingsFromStorage = (): Partial<PersistedExportSettings> => {
  if (typeof window === 'undefined') return {};
  try {
    const stored = localStorage.getItem(EXPORT_SETTINGS_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

const saveExportSettingsToStorage = (settings: PersistedExportSettings) => {
  try {
    localStorage.setItem(EXPORT_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    console.error('Error saving export settings:', error);
  }
};

function generateRowId(): string {
  return `row-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

type Deps = {
  global: typeof globalStores;
};

type State = {
  promptRows: MotionPromptRow[];
  preferredVariationCount: number;
  model: BatchGenerationModel;
  duration: number;
  loop: boolean;
  jobName: string;
  /** Set when Run process is blocked for empty job name; cleared when the name is edited. */
  jobNameValidationError: boolean;
  fileType: ExportType;
  frameRate: FrameRate;
  keyframeSimplification: 'none' | 'simplify';
  includeMesh: boolean;
  uploadModalState: UploadModalState;
};

const savedSettings = loadExportSettingsFromStorage();

const createInitialRow = (variationCount: number): MotionPromptRow => ({
  id: generateRowId(),
  prompt: '',
  variationCount,
});

const initialState: State = {
  promptRows: [
    {
      id: generateRowId(),
      prompt: '',
      variationCount: 0,
    },
  ],
  preferredVariationCount: 0,
  model: 'swing',
  duration: 5,
  loop: false,
  jobName: '',
  jobNameValidationError: false,
  fileType: savedSettings.fileType ?? 'FBX_UNREAL',
  frameRate: savedSettings.frameRate ?? 60,
  keyframeSimplification: savedSettings.keyframeSimplification ?? 'simplify',
  includeMesh: savedSettings.includeMesh ?? true,
  uploadModalState: 'idle',
};

class batchGenerationPromptStore extends BaseStore<State, Deps> {
  constructor(deps: Deps) {
    super(initialState, deps);
  }

  reset() {
    Object.assign(this.state, {
      ...initialState,
      promptRows: [],
      fileType: savedSettings.fileType ?? initialState.fileType,
      frameRate: savedSettings.frameRate ?? initialState.frameRate,
      keyframeSimplification:
        savedSettings.keyframeSimplification ??
        initialState.keyframeSimplification,
      includeMesh: savedSettings.includeMesh ?? initialState.includeMesh,
    });
  }

  addMotionRow() {
    this.state.promptRows = [
      ...this.state.promptRows,
      createInitialRow(this.state.preferredVariationCount),
    ];
  }

  removeMotionRow(id: string) {
    this.state.promptRows = this.state.promptRows.filter((r) => r.id !== id);
  }

  updateMotionRow(
    id: string,
    field: keyof Omit<MotionPromptRow, 'id'>,
    value: string | number | boolean,
  ) {
    const row = this.state.promptRows.find((r) => r.id === id);
    if (row && field in row) {
      (row as Record<string, unknown>)[field] = value;
      if (
        field === 'variationCount' &&
        typeof value === 'number' &&
        value >= 1
      ) {
        this.state.preferredVariationCount = value;
      }
    }
  }

  updateJobName(jobName: string) {
    this.state.jobNameValidationError = false;
    this.state.jobName = jobName;
  }

  updateModel(model: BatchGenerationModel) {
    this.state.model = model;
  }

  updateDuration(duration: number) {
    this.state.duration = Math.min(8, Math.max(3, duration));
  }

  updateLoop(loop: boolean) {
    this.state.loop = loop;
  }

  updateFileType(fileType: ExportType) {
    this.state.fileType = fileType;
    if (fileType === 'BVH') {
      this.state.includeMesh = false;
      this.state.keyframeSimplification = 'none';
    } else if (fileType === 'MA' || fileType === 'MB') {
      this.state.includeMesh = true;
      this.state.keyframeSimplification = 'simplify';
    }
    this.saveExportSettings();
  }

  updateFrameRate(frameRate: FrameRate) {
    this.state.frameRate = frameRate;
    this.saveExportSettings();
  }

  updateKeyframeSimplification(value: 'none' | 'simplify') {
    this.state.keyframeSimplification = value;
    this.saveExportSettings();
  }

  updateIncludeMesh(includeMesh: boolean) {
    this.state.includeMesh = includeMesh;
    this.saveExportSettings();
  }

  async runProcess() {
    if (this.deps.global.authStore.state.authStatus !== 'user') {
      this.deps.global.requestAppletSignInModal();
      return;
    }

    if (!this.state.jobName.trim()) {
      this.state.jobNameValidationError = true;
      globalStores.noticesStore.error('Job name required', 6000);
      return;
    }

    const rowsWithPrompts = this.state.promptRows.filter(
      (row) => row.prompt.trim().length > 0,
    );
    if (rowsWithPrompts.length === 0) {
      globalStores.noticesStore.error('Add at least one motion prompt.');
      return;
    }

    for (const row of rowsWithPrompts) {
      const v = row.variationCount;
      if (v == null || !Number.isInteger(v) || v < 1 || v > 100) {
        globalStores.noticesStore.error(
          'Each motion must have a valid number of variations (1–100).',
        );
        return;
      }
    }

    const totalVariations = rowsWithPrompts.reduce(
      (sum, row) => sum + (row.variationCount ?? 0),
      0,
    );
    if (totalVariations > 100) {
      globalStores.noticesStore.error(
        'Total variations across all motions must be 100 or less.',
      );
      return;
    }

    const prompts = this.state.promptRows.flatMap((row) =>
      row.prompt.trim()
        ? Array.from({ length: row.variationCount }, () => row.prompt.trim())
        : [],
    );

    let apiKey: string;
    try {
      const apiKeyData = await waitForAsyncState(
        this.deps.global.userStore.state,
        (s) => s.apiKey,
      );
      if (!apiKeyData) {
        globalStores.noticesStore.error(
          'Failed to authenticate. Please try again.',
        );
        return;
      }
      apiKey = apiKeyData.apiKey;
    } catch (err) {
      console.error('Failed to get API key:', err);
      globalStores.noticesStore.error(
        'Failed to authenticate. Please try again.',
      );
      return;
    }

    this.state.uploadModalState = 'starting_process';

    try {
      const exportSettings: ExportSettings = {
        characterID: 'char-upload-Axel',
        exportType: this.mapExportType(this.state.fileType),
        forward: '-Z',
        up: 'Y',
        frameRate: this.state.frameRate,
        frameStepSize: 1,
        moveInPlace: true,
        includeMesh: this.state.includeMesh,
        keyframeCleaning:
          this.state.keyframeSimplification === 'simplify'
            ? 'simplify'
            : 'none',
      };

      const { data: batchData, error: batchError } = await postMotionFromText(
        {
          batchName: this.state.jobName || undefined,
          createdByUser: this.deps.global.userStore.state.userInfo?.userID,
          exportSettings,
          prompts,
          loop: this.state.loop,
          requestedModel: this.state.model,
          swingProperties: {
            cfgScale: 7.5,
            duration: this.state.duration,
          },
          subscribers: [this.deps.global.userStore.state.userInfo.email],
        },
        apiKey,
      );

      if (batchError || !batchData) {
        if (
          batchError instanceof APIError &&
          handleOrgCreditBlockApiError(
            batchError,
            globalStores.userStore.state.organizationInfo,
          )
        ) {
          this.state.uploadModalState = 'idle';
          return;
        }
        globalStores.noticesStore.error(
          batchError?.message || 'Failed to start processing',
        );
        this.state.uploadModalState = 'idle';
        return;
      }

      this.state.uploadModalState = 'idle';
      navigateTo(`/generation/${batchData.batchID}`);
    } catch (error) {
      console.error('Error running process:', error);
      this.state.uploadModalState = 'idle';
    }
  }

  private saveExportSettings() {
    saveExportSettingsToStorage({
      fileType: this.state.fileType,
      frameRate: this.state.frameRate,
      keyframeSimplification: this.state.keyframeSimplification,
      includeMesh: this.state.includeMesh,
    });
  }

  private mapExportType(type: ExportType): ApiExportType {
    const mapping: Record<ExportType, ApiExportType> = {
      BVH: 'bvh',
      FBX_MAYA: 'fbx-maya',
      FBX_BLENDER: 'fbx-blender',
      FBX_UNREAL: 'fbx-unreal',
      FBX_ROBLOX: 'fbx-roblox',
      GLB: 'glb',
      MA: 'ma',
      MB: 'mb',
    };
    return mapping[type];
  }
}

export const { useStore, withProvider, Context } = createStoreProvider({
  Store: batchGenerationPromptStore,
  displayName: 'batchGenerationPromptStore',
  staticDeps: { global: globalStores },
});

export type { batchGenerationPromptStore };
